import { useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
// import { usePaymentsWetag } from '@wetags/payments';
import { Commands1 } from '@frontend/api-commands';
import { PaymentsView1 } from '@frontend/api-payments-view';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { paymentsSentry } from '@frontend/payments-hooks';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { Text, useAlert, useModalControl } from '@frontend/design-system';
import { isSyncCompleted } from '../../../hooks/settings/use-data-sources';
import { useGenerateColumns } from '../../../hooks/use-generate-columns';
import { COLLECTIONS_PORTAL_TARGET_ID } from '../../../types/payment-request';
import { BulkCreateModal, BulkCreateStepId } from '../BulkCreateModal/bulk-create-modal';
import { BULK_CREATE_STEPS } from '../BulkCreateModal/bulk-create-steps-key-title';
import { CollectionColumn, generateColumns } from '../common/generate-columns';
import { CollectionsSummary } from './collections-summary';
import { Filters } from './filters';
import { DesktopList } from './list/desktop-list';
import { MobileList } from './list/mobile-list';
import { styles } from './styles';
import { MobileToolBar } from './top-toolbar/mobile-toolbar';

type CollectionListProps = {
  onDismissBanner?: () => void;
};

const USE_NEW_COLLECTIONS_API = false; // TODO: Remove this once all the other PR's are merged.
const isLoadingWeTag = false; // TODO: Remove this once all the other PR's are merged.

export const CollectionList = ({ onDismissBanner }: CollectionListProps = {}) => {
  const { selectedLocationIds } = useAppScopeStore();
  const { t } = useTranslation('payments');
  // const activeLocationId = selectedLocationIds[0];
  // const { useWetag } = usePaymentsWetag({ locationId: activeLocationId, namespace: 'payments' });
  // const [USE_NEW_COLLECTIONS_API, isLoadingWeTag] = useWetag('enable-payments-collections-batch');
  const columns = useGenerateColumns<CollectionColumn>(generateColumns, false, undefined, {
    hideLocationNameColumn: selectedLocationIds.length === 1,
  });
  const alert = useAlert();
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });
  const [selectedPatientsIds, setSelectedPatientsIds] = useState<string[]>([]);
  const [modalInitialStep, setModalInitialStep] = useState<BulkCreateStepId>(BULK_CREATE_STEPS.SEND_SMS.id);
  const { triggerProps, modalProps } = useModalControl();
  const queryStartTimeRef = useRef<number>(Date.now());

  const { data: sourceInfo } = PaymentsView1.Queries.useGetLocationsSourceInfoQuery({
    request: { locationIds: selectedLocationIds },
    options: { enabled: selectedLocationIds.length > 0 && !isMobile },
  });

  const lastSyncCompletedAt = useMemo(() => {
    if (!sourceInfo?.locationsSourceInfo) return undefined;

    for (const { sources: locationSources } of sourceInfo.locationsSourceInfo) {
      if (!locationSources) continue;
      for (const source of locationSources) {
        if (source?.capability?.bulkCollectionEnabled && isSyncCompleted(source)) {
          return source.lastSyncCompletedAt;
        }
      }
    }
    return undefined;
  }, [sourceInfo]);

  const { data: collections, isLoading } = Commands1.Queries.useGetPatientsBalanceQuery({
    request: {
      locationIds: selectedLocationIds,
    },
    options: {
      enabled: !USE_NEW_COLLECTIONS_API && !isLoadingWeTag,
      onError: () => alert.error('Error fetching collections'),
      refetchOnMount: 'always',
      onSuccess: () => {
        const queryTime = Date.now() - queryStartTimeRef.current;
        paymentsSentry.log('Collections query completed', {
          queryTimeMs: queryTime,
          locations: JSON.stringify(selectedLocationIds),
        });
      },
    },
  });

  const totalAmountToCollect = useMemo(
    () => collections?.patientBalances?.reduce((acc, collection) => acc + (collection.balance ?? 0), 0),
    [collections]
  );

  const toggleSelection = (patientId: string) => {
    setSelectedPatientsIds((prev) => {
      if (prev.includes(patientId)) {
        return prev.filter((p) => p !== patientId);
      } else {
        return [...prev, patientId];
      }
    });
  };

  const portalTarget = document.getElementById(COLLECTIONS_PORTAL_TARGET_ID);

  return (
    <div css={[styles.collectionList.container, isMobile && styles.collectionList.containerMobile]}>
      {!isMobile &&
        lastSyncCompletedAt &&
        portalTarget &&
        createPortal(
          <div css={styles.collectionList.syncInfo}>
            <Icon name='sync-small' color='light' />
            <Text color='light'>
              {t('Collection balances last synced ')} {formatDate(lastSyncCompletedAt, 'MMM D, YYYY h:mm a')}
            </Text>
          </div>,
          portalTarget
        )}
      <Filters />
      <CollectionsSummary balance={totalAmountToCollect} />
      {isMobile ? (
        <>
          {collections?.patientBalances && (
            <MobileToolBar
              count={selectedPatientsIds.length}
              onClear={() => {
                setSelectedPatientsIds([]);
              }}
              onSelectAll={() => {
                setSelectedPatientsIds(
                  collections.patientBalances?.filter((pb) => !!pb.phoneNumber).map((pb) => pb.patientId) || []
                );
              }}
              selectionState={
                selectedPatientsIds.length === collections.patientBalances.length
                  ? 'all'
                  : selectedPatientsIds.length > 0
                  ? 'some'
                  : 'none'
              }
              isSelectionDisabled={collections.patientBalances.filter((pb) => !!pb.phoneNumber).length === 0}
              triggerProps={triggerProps}
              toggleInitialStep={setModalInitialStep}
            />
          )}
          <MobileList
            isLoading={isLoading}
            collections={collections}
            toggleSelection={toggleSelection}
            selected={selectedPatientsIds}
            useNewApi={USE_NEW_COLLECTIONS_API}
          />
        </>
      ) : (
        <DesktopList
          isLoading={isLoading}
          columns={columns}
          collections={collections}
          selected={selectedPatientsIds}
          onSelectionChange={setSelectedPatientsIds}
          triggerProps={triggerProps}
          toggleInitialStep={setModalInitialStep}
          onDismissBanner={onDismissBanner}
          useNewApi={USE_NEW_COLLECTIONS_API}
        />
      )}
      {modalProps.show && (
        <BulkCreateModal
          initialStep={modalInitialStep}
          locationIds={selectedLocationIds}
          modalProps={modalProps}
          selected={collections?.patientBalances?.filter((pb) => selectedPatientsIds.includes(pb.patientId)) || []}
        />
      )}
    </div>
  );
};
